// Textos de la pestaña Logística 2 (formato nuevo): solo presentación.
//
// No calcula nada de negocio: toma el horario, las filas del personal y el
// estado del transporte que ya dan utils/quienVaEvento.js y el backend, y los
// escribe en lenguaje de oficina (horas redondeadas a 5 minutos, direcciones
// cortas, una frase para el transporte, una línea para los camareros).
// El redondeo es solo visual: los minutos guardados no cambian.
import { formatoMinutos, horaConDia, minutosDeHora, horaTexto, personasFila, ORIGEN_SOLAPE, tarifaTransportePersonal } from './quienVaEvento';

const entero = (v) => {
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? 0 : n;
};

// Minutos redondeados a 5 (al más cercano)
export const redondearA5 = (min) => Math.round((Number(min) || 0) / 5) * 5;

// "06:23" -> "06:25"; "16:52" -> "16:50" (solo para enseñar)
export const horaRedondeada = (hora) => {
  const m = minutosDeHora(hora);
  if (m === null) return hora || '';
  return horaTexto(redondearA5(m));
};

// Hora redondeada con el día si cambia ("00:15 (+1)")
export const horaRedondeadaConDia = (hora, dia = 0) => {
  const m = minutosDeHora(hora);
  if (m === null) return hora || '';
  const r = redondearA5(m);
  return horaConDia(horaTexto(r), (Number(dia) || 0) + (r >= 1440 ? 1 : 0));
};

// "10 h 30 min" con los minutos redondeados a 5
export const duracionRedondeada = (min) => formatoMinutos(redondearA5(min));

// Quita el año de los nombres de menú ("Lunch 8 platos 2026" -> "Lunch 8 platos")
export const sinAnio = (nombre) => String(nombre || '').replace(/\s*\b(19|20)\d{2}\b/g, '').replace(/\s{2,}/g, ' ').trim();

// Números con coma decimal ("31,8")
export const numeroEs = (n, decimales = 1) => (Number(n) || 0).toLocaleString('es-ES', {
  minimumFractionDigits: 0, maximumFractionDigits: decimales,
});

// "20 €" si es entero, "20,50 €" si no
export const eurosCorto = (n) => {
  const v = Number(n) || 0;
  const entera = Math.abs(v - Math.round(v)) < 0.005;
  return `${v.toLocaleString('es-ES', { minimumFractionDigits: entera ? 0 : 2, maximumFractionDigits: entera ? 0 : 2 })} €`;
};

export const formatEuros = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

// Horas de una fila del personal: "3 h", "4,5 h"
export const horasTexto = (h) => `${numeroEs(h, 2)} h`;

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

/**
 * Dirección corta a partir de la dirección de Google:
 * "Av. de la Osa Mayor, 4, Moncloa - Aravaca, 28023 Madrid, España"
 *   -> { linea: "Av. de la Osa Mayor, 4 · 28023 Madrid (Aravaca)", destino: "Aravaca" }
 * Sin código postal, la dirección tal cual (sin "España").
 */
export const direccionCorta = (direccion, municipio = null) => {
  const texto = String(direccion || '').trim();
  if (!texto) return { linea: '', destino: municipio || '' };
  const partes = texto.split(',').map(x => x.trim()).filter(Boolean).filter(x => !/^espa(ñ|n)a$/i.test(x));
  const iCp = partes.findIndex(x => /^\d{5}\b/.test(x));
  if (iCp < 0) return { linea: partes.join(', '), destino: municipio || '' };
  const cpCiudad = partes[iCp];
  const ciudad = cpCiudad.replace(/^\d{5}\s*/, '').trim();
  // Calle y número (el número suele ir en la segunda parte)
  let finCalle = 1;
  if (partes.length > 1 && iCp > 1 && /^\d+[\w-]*$/.test(partes[1])) finCalle = 2;
  const calle = partes.slice(0, Math.min(finCalle, iCp)).join(', ');
  // Barrio o distrito entre la calle y el código postal ("Moncloa - Aravaca" -> "Aravaca")
  const medio = partes.slice(finCalle, iCp);
  const barrio = medio.length > 0 ? medio[medio.length - 1].split(/\s+-\s+/).pop().trim() : '';
  const lugar = barrio && barrio.toLowerCase() !== ciudad.toLowerCase() ? `${cpCiudad} (${barrio})` : cpCiudad;
  return {
    linea: calle && iCp > 0 ? `${calle} · ${lugar}` : lugar,
    destino: barrio || ciudad || municipio || '',
  };
};

// Enlace a Google Maps con la dirección (o el lugar)
export const urlMapa = (presupuesto) => {
  const destino = presupuesto?.direccion_exacta || presupuesto?.lugar_evento;
  if (!destino) return null;
  const base = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destino)}`;
  return presupuesto?.place_id ? `${base}&query_place_id=${encodeURIComponent(presupuesto.place_id)}` : base;
};

// Google Maps en modo "cómo llegar" al evento: solo cuando la dirección viene
// del buscador de Google (coordenadas, punto exacto, o dirección exacta). Con
// el nombre del lugar a secas no hay botón (regla del usuario, 02-oct-2026).
// Abre la app de Maps en el móvil y la web en el ordenador.
export const urlIrAlEvento = (presupuesto) => {
  const c = presupuesto?.coordenadas;
  const lat = Number(c?.lat), lng = Number(c?.lng);
  let destino = null;
  if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) destino = `${lat},${lng}`;
  else destino = presupuesto?.direccion_exacta || null;
  if (!destino) return null;
  const base = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destino)}&travelmode=driving`;
  return presupuesto?.place_id ? `${base}&destination_place_id=${encodeURIComponent(presupuesto.place_id)}` : base;
};

// Códigos postales del distrito Centro de Madrid (zona de bajas emisiones de
// Madrid Central). Solo es una pista: se marca a mano.
const CP_MADRID_CENTRAL = ['28004', '28005', '28012', '28013', '28014'];
export const pistaMadridCentral = (direccion) => {
  const m = String(direccion || '').match(/\b(28\d{3})\b/);
  if (!m) return null;
  return CP_MADRID_CENTRAL.includes(m[1]) ? 'puede' : 'no';
};

/**
 * Horario de quien va en 4 a 6 líneas (8 si se va y vuelve a recoger), con las
 * horas redondeadas a 5 minutos. `horario`: el de horarioJornada.
 * Devuelve [{ clave, hora, texto }].
 */
export const lineasHorario = (horario, { destino = '' } = {}) => {
  if (!horario || !Array.isArray(horario.bloques) || horario.bloques.length === 0) return [];
  const lineas = [];
  const hacia = destino ? `hacia ${destino}` : 'hacia el evento';
  const fechaCorta = (f) => (f ? ` (${String(f).slice(8, 10)}/${String(f).slice(5, 7)})` : '');
  const tramosDe = (b) => Object.fromEntries((b.tramos || []).map(t => [t.clave, t]));
  const poner = (clave, t, texto, conFin = false) => {
    if (!t) return;
    lineas.push({
      clave,
      hora: conFin ? horaRedondeadaConDia(t.fin, t.dia_fin) : horaRedondeadaConDia(t.inicio, t.dia_inicio),
      texto,
    });
  };
  const b0 = tramosDe(horario.bloques[0]);
  const dosBloques = horario.bloques.length > 1;
  poner('carga', b0.carga, entero(b0.carga?.minutos) > 0 ? 'En la nave, carga' : 'En la nave');
  poner('ida', b0.ida, `Sale ${hacia}${entero(b0.ida?.minutos) > 0 ? ` (${formatoMinutos(b0.ida.minutos)})` : ''}`);
  poner('montaje', b0.montaje, entero(b0.montaje?.minutos) > 0 ? 'Llega y monta' : 'Llega');
  poner('evento', b0.evento, 'Empieza el evento');
  // Termina en el mismo bloque (se queda) o en el de la recogida
  const final = dosBloques ? tramosDe(horario.bloques[1]) : b0;
  if (dosBloques || entero(b0.vuelta_montaje?.minutos) > 0) {
    poner('vuelta_montaje', b0.vuelta_montaje, 'Vuelve a la nave');
    const fecha = horario.otroDia ? fechaCorta(horario.bloques[1]?.fecha) : '';
    poner('ida_recogida', final.ida_recogida, `Sale a recoger${fecha}`);
    poner('desmontaje', final.desmontaje, 'Llega y recoge');
  } else {
    poner('desmontaje', final.desmontaje, 'Termina y recoge');
  }
  if (final.descarga) {
    poner('descarga', final.descarga, entero(final.descarga.minutos) > 0
      ? `De vuelta en la nave, descarga hasta las ${horaRedondeadaConDia(final.descarga.fin, final.descarga.dia_fin)}`
      : 'De vuelta en la nave');
  }
  return lineas;
};

// Servicios del día y montajes intermedios (hora por servicio), sin el año en los nombres
export const serviciosDelDia = (lineaDia) => {
  if (!lineaDia || !Array.isArray(lineaDia.items)) return [];
  return lineaDia.items.filter(it => it.tipo !== 'espera').map((it, i) => ({
    clave: `item-${i}`,
    texto: it.tipo === 'montaje'
      ? `${horaTexto(it.inicio)} a ${horaTexto(it.fin)} Monta ${sinAnio(it.nombre)} (ya está allí)`
      : `${horaTexto(it.inicio)} a ${horaTexto(it.fin)} ${sinAnio(it.nombre)}`,
    servicio: it.tipo === 'servicio',
  }));
};

// Texto del aviso de solape de montaje
export const textoSolape = (s) => (
  `El montaje de ${sinAnio(s.nombre)} coincide con ${sinAnio(s.servicio_anterior) || 'el servicio anterior'} (${formatoMinutos(s.minutos)})`
);

const esFilaSolape = (f) => f?.origen === ORIGEN_SOLAPE;

// Fila que no lleva personal nunca (sin ratio, sin servicio por horas, 0 por defecto y sin tocar)
export const filaSinPersonal = (f) => {
  const d = f?.defecto || {};
  return !esFilaSolape(f) && !f?.por_horas && !(Number(f?.ratio_camareros) > 0) && !(Number(f?.ratio_extras) > 0)
    && entero(d.camareros) + entero(d.extras) === 0 && entero(f?.camareros) + entero(f?.extras) === 0
    && !(f?.editados || []).length;
};

const nombreServicio = (f) => (f?.por_horas && f.por_horas.por_unidad === 'camarero'
  ? 'El servicio de camarero' : `${sinAnio(f?.nombre)}`);

const textoPersonas = (f) => {
  const cam = entero(f?.camareros);
  const ext = entero(f?.extras);
  const partes = [];
  if (cam > 0) partes.push(plural(cam, 'camarero', 'camareros'));
  if (ext > 0) partes.push(plural(ext, 'extra', 'extras'));
  return partes.join(' + ');
};

// "3 camareros · 4 h" o, con tandas de camareros, "2 camareros de 11:00 a
// 15:00 y 3 de 13:00 a 17:00" (+ los extras con sus horas)
const textoPersonasHoras = (f) => {
  const tandas = Array.isArray(f?.tandas) ? f.tandas : [];
  if (tandas.length === 0) return `${textoPersonas(f)} · ${horasTexto(f.horas)}`;
  const texto = tandas.map((t, i) => {
    const n = entero(t.camareros);
    return `${i === 0 ? plural(n, 'camarero', 'camareros') : n} de ${t.hora_inicio} a ${t.hora_fin}`;
  }).join(' y ');
  const ext = entero(f.extras);
  return ext > 0 ? `${texto} + ${plural(ext, 'extra', 'extras')} · ${horasTexto(f.horas)}` : texto;
};

/**
 * Camareros en lenguaje de oficina a partir de las filas del personal del menú
 * (sin las del solape, que van en el horario).
 * - Sin nadie aparte: una línea ("No hacen falta aparte. El servicio de
 *   camarero (3 h) lo hace Brandon." o "No hacen falta camareros.").
 * - Con personal: una línea por menú que lo lleva.
 */
export const resumenCamareros = (filas = [], nombre = '') => {
  const quien = nombre || 'quien va';
  const propias = (filas || []).filter(f => !esFilaSolape(f));
  const conPersonal = propias.filter(f => personasFila(f) > 0);
  const cubiertas = propias.filter(f => f.cubre_responsable && entero(f.camareros) + entero(f.extras) > 0);
  const textoCubiertas = cubiertas.map(f => `${nombreServicio(f)} (${horasTexto(f.horas)}) lo hace ${quien}.`).join(' ');
  if (conPersonal.length === 0) {
    return {
      hacenFalta: false,
      texto: cubiertas.length > 0 ? `No hacen falta aparte. ${textoCubiertas}` : 'No hacen falta camareros.',
      lineas: [],
    };
  }
  return {
    hacenFalta: true,
    texto: textoCubiertas,
    lineas: conPersonal.map(f => ({ clave: f.clave, texto: `${sinAnio(f.nombre)}: ${textoPersonasHoras(f)}` })),
  };
};

/**
 * Transporte al cliente en una frase, con el estado del backend
 * (estadoReglas.transporte). { cobra, texto, detalle }.
 */
export const textoTransporteCliente = (t) => {
  if (!t) return { cobra: false, texto: 'Calculando el transporte...', detalle: '' };
  const cobrar = !!(t.cobrar ?? t.linea);
  const mc = t.linea_madrid_central || null;
  const textoMc = mc ? (mc.es_cortesia ? ' + Madrid Central regalado' : ` + ${eurosCorto(mc.total)} Madrid Central`) : '';
  if (!cobrar) {
    let motivo = '';
    if (t.disponible && t.zona_gratuita) motivo = ` (${t.municipio || 'zona'}, zona gratuita)`;
    else if (t.disponible && Number(t.importe) > 0) motivo = ` (por zona serían ${eurosCorto(t.importe)})`;
    return { cobra: false, texto: `No se cobra transporte${motivo}${textoMc}`, detalle: '' };
  }
  if (!t.linea) {
    return { cobra: true, texto: 'Se cobra transporte, pero falta calcularlo', detalle: t.motivo || 'Falta la ruta desde la nave.' };
  }
  const principal = t.linea.es_cortesia ? 'Transporte regalado' : `Se cobran ${eurosCorto(t.linea.total)} de transporte`;
  let detalle = '';
  if (t.importe_manual) {
    if (t.disponible && t.zona_gratuita) detalle = 'Importe puesto a mano; por zona sería gratis.';
    else if (t.disponible) detalle = `Importe puesto a mano; por zona serían ${eurosCorto(t.importe)}.`;
    else detalle = 'Importe puesto a mano.';
  } else if (t.zona_gratuita) {
    detalle = `${t.municipio || 'Zona'}: zona gratuita.`;
  } else if (t.zona) {
    detalle = `Precio de la zona ${t.zona}${t.municipio ? ` (${t.municipio})` : ''}.`;
  }
  const iva = (!t.linea.es_cortesia || (mc && !mc.es_cortesia)) ? ' + IVA' : '';
  return { cobra: true, texto: `${principal}${textoMc}${iva}`, detalle };
};

// Furgonetas asignadas en una línea ("9492FHM Mercedes Vito", "Alquiler: Europcar")
export const textoFurgonetas = (vehiculos = []) => (vehiculos || []).map(v => (v?.es_alquiler || v?.tipo === 'alquiler'
  ? `Alquiler: ${v.empresa_alquiler || 'sin empresa'}`
  : [v?.matricula, v?.marca, v?.modelo].filter(Boolean).join(' '))).filter(Boolean).join(', ');

// "Guardado por Manu, 27/09 a las 12:30"
export const textoGuardadoPor = (quien, cuando) => {
  if (!cuando) return '';
  const d = new Date(cuando);
  if (Number.isNaN(d.getTime())) return `Guardado por ${quien || 'alguien'}`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `Guardado por ${quien || 'alguien'}, ${dd}/${mm} a las ${hh}:${mi}`;
};

/**
 * Coste interno de la ruta desde la nave (el de la pestaña Logística): tiempo
 * de ida y vuelta de quien va a su tarifa (tarifaTransportePersonal) y gasoil
 * del backend (`transporte.coste_combustible`); el total es el guardado en el
 * presupuesto (`coste_transporte`). Con un socio sin coste, solo el gasoil.
 * Devuelve null si no hay ruta.
 */
export const costeRutaInterno = ({ presupuesto, reglas, transporte, sinCoste = false }) => {
  const p = presupuesto || {};
  const km = parseFloat(p.distancia_km) || 0;
  if (km <= 0) return null;
  const minutosIda = entero(p.tiempo_viaje_min);
  const minutosIdaVuelta = minutosIda * 2;
  const tarifa = reglas ? tarifaTransportePersonal(p.quien_va_evento?.modo, reglas) : null;
  const gasoil = transporte?.coste_combustible != null ? Number(transporte.coste_combustible) || 0 : null;
  const personal = tarifa ? (sinCoste ? 0 : Math.round(minutosIdaVuelta * tarifa.tarifaHora / 60 * 100) / 100) : null;
  const total = sinCoste ? gasoil : (Number(p.coste_transporte) || 0);
  return {
    km, minutosIda, minutosIdaVuelta, kmIdaVuelta: km * 2, tarifa, personal, gasoil, total, sinCoste,
    detallePersonal: tarifa ? (sinCoste
      ? `${minutosIdaVuelta} min, socio sin coste`
      : `${minutosIdaVuelta} min × ${formatEuros(tarifa.tarifaMinuto)}/min, ${tarifa.etiqueta}`) : null,
  };
};

// "Ruta desde la nave: 31,8 km · 37 min por trayecto · nos cuesta 25,60 € (19,24 € personal + 6,36 € gasoil)"
// Con `corto`: sin el desglose ("nos cuesta 25,60 €"; con socio, "(solo gasoil)")
export const textoRutaInterna = (c, { corto = false } = {}) => {
  if (!c) return '';
  const base = `${numeroEs(c.km)} km · ${formatoMinutos(c.minutosIda)} por trayecto`;
  if (c.total == null) return base;
  let desglose = '';
  if (corto) desglose = c.sinCoste ? ' (solo gasoil)' : '';
  else if (c.sinCoste && c.gasoil != null) desglose = ' (gasoil; personal sin coste)';
  else if (c.personal != null && c.gasoil != null) desglose = ` (${formatEuros(c.personal)} personal + ${formatEuros(c.gasoil)} gasoil)`;
  return `${base} · nos cuesta ${formatEuros(c.total)}${desglose}`;
};
